import { METRIC_CATEGORY_ORDER, MetricCategory } from '@chiroup/core/constants/metricCategories';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useEventLogClinicDaily from '../../hooks/useEventLogClinicDaily';
import useEventLogClinicSummary from '../../hooks/useEventLogClinicSummary';
import useEventLogUsers from '../../hooks/useEventLogUsers';
import Header from '../layout/Header';
import CategoryTabs from './CategoryTabs';
import DateRangePicker, { computeRange, RangePreset } from './DateRangePicker';
import MetricDrawer from './MetricDrawer';
import MetricGrid from './MetricGrid';
import SummaryCard from './SummaryCard';
import UsersStrip from './UsersStrip';

const VALID_PRESETS = new Set<RangePreset>(['7d', '30d', '90d']);
const VALID_CATEGORIES = new Set<MetricCategory>(METRIC_CATEGORY_ORDER);

const EventLogClinicDetail: React.FC = () => {
  const { clinicId } = useParams<{ clinicId: string }>();
  const id = Number(clinicId);

  const [searchParams, setSearchParams] = useSearchParams();
  const rangeFromUrl = (searchParams.get('range') as RangePreset) || '7d';
  const range: RangePreset = VALID_PRESETS.has(rangeFromUrl) ? rangeFromUrl : '7d';
  const tabFromUrl = (searchParams.get('tab') as MetricCategory) || 'clinical';
  const activeTab: MetricCategory = VALID_CATEGORIES.has(tabFromUrl) ? tabFromUrl : 'clinical';
  const userFilter = searchParams.get('user') || undefined;

  const { startDate, endDate } = useMemo(() => computeRange(range), [range]);

  const [drawerMetric, setDrawerMetric] = useState<string | null>(null);

  const { data: summary, isFetching: summaryLoading } = useEventLogClinicSummary(
    id,
    startDate,
    endDate,
    userFilter
  );
  const { data: daily, isFetching: dailyLoading } = useEventLogClinicDaily(
    id,
    startDate,
    endDate,
    userFilter
  );
  const { data: usersData, isFetching: usersLoading } = useEventLogUsers(id, startDate, endDate);

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: false });
  };

  const onChangeRange = (next: RangePreset) => updateParam('range', next);
  const onChangeTab = (next: MetricCategory) => updateParam('tab', next);
  const onSelectUser = (userId: string | null) => updateParam('user', userId);

  const filteredUserName = useMemo(() => {
    if (!userFilter || !usersData) return undefined;

    return usersData.find(u => u.userId === userFilter)?.userName;
  }, [userFilter, usersData]);

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-3">
            <Link to="/event-logs" className="text-gray-400 hover:text-gray-600">
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <span>{summary?.clinic?.name || `Clinic ${clinicId}`}</span>
          </div>
        }
        rightSide={<DateRangePicker active={range} onChange={onChangeRange} />}
      />

      <div className="p-6 space-y-6">
        <SummaryCard
          summary={summary}
          daily={daily}
          loading={summaryLoading || dailyLoading}
          filteredUserName={filteredUserName}
        />

        <div>
          <CategoryTabs
            active={activeTab}
            onChange={onChangeTab}
            categoryTotals={
              summary?.categoryTotals || {
                schedule: 0,
                clinical: 0,
                billing: 0,
                patients: 0,
                setup: 0,
                support: 0,
              }
            }
          />
          <div className="pt-5">
            <MetricGrid
              category={activeTab}
              metrics={summary?.metrics || {}}
              previousMetrics={summary?.previousMetrics || {}}
              dailyByMetric={daily?.dailyByMetric || {}}
              onMetricClick={setDrawerMetric}
              loading={summaryLoading && !summary}
            />
          </div>
        </div>

        <UsersStrip
          users={usersData || []}
          loading={usersLoading && !usersData}
          selectedUserId={userFilter || null}
          onSelectUser={onSelectUser}
        />
      </div>

      <MetricDrawer
        open={!!drawerMetric}
        onClose={() => setDrawerMetric(null)}
        clinicId={id}
        metricKey={drawerMetric}
        startDate={startDate}
        endDate={endDate}
        userId={userFilter}
      />
    </>
  );
};

export default EventLogClinicDetail;
